"use client"

import { useEffect, useState } from "react"
import api from "../services/api"
import PricingCard from "../components/payment/PricingCard"
import PricingComparisonTable from "../components/payment/PricingComparisonTable"
import PaymentMethodModal from "../components/payment/PaymentMethodModal"
import DashboardLayout from "../components/Layout/DashboardLayout"
import MiniFooter from "../components/Layout/miniFooter"
import WhatsAppChat from "../components/support/WhatsAppChat"

export default function UpgradePage() {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentUserPlan, setCurrentUserPlan] = useState("free")
  const [billingType, setBillingType] = useState("monthly")
  const [ currency ] = useState("NGN");

const [showPaymentModal, setShowPaymentModal] = useState(false)
const [selectedPlan, setSelectedPlan] = useState(null)


  const BRAND = {
    primary: "#3B82F6",
    primaryDark: "#2563EB",
    primarySoft: "rgba(59, 130, 246, 0.1)",
    success: "#22C55E",
    warning: "#F59E0B",
    muted: "#6B7280",
    text: "#1F2937",
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        const userRes = await api.get("/subscription/status")
        setCurrentUserPlan(userRes.data.plan)

        const plansRes = await api.get("/subscription/plans")
        setPlans(
          Object.entries(plansRes.data).map(([key, plan]) => ({
            key,
            title: plan.title,
            price: plan.pricing,
            features: plan.features,
            limit: plan.limit,
            monthlyUrls: plan.monthlyUrls,
            trafficLimit: plan.trafficLimit,
          }))
        )
      } catch (err) {
        console.error("Failed to fetch plans:", err)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])


const openPaymentModal = (planKey, billingType) => {
  if (!planKey || !billingType) return;

  const plan = plans.find(p => p.key === planKey);
  if (!plan) return;

  let amount = plan.price[billingType];

  // OPTIONAL: if currency is NGN and prices are USD
  if (currency === "NGN") {
    const USD_TO_NGN = 1500; // example – ideally from backend
    amount = Math.round(amount * USD_TO_NGN);
  }

  setSelectedPlan({
    planKey,
    billingType,
    lowestAmount: amount
  });

  setShowPaymentModal(true);
};




useEffect(() => {
  const handler = async (e) => {
    if (!e?.detail) return;

    const { method, planKey, billingType, currency } = e.detail;

    if (!method || !planKey || !billingType) {
      alert("Invalid payment request");
      return;
    }

    const endpoint =
      method === "paystack"
        ? "/payment/create-session"
        : "/stripe/create-checkout-session";

    try {
      const res = await api.post(
        endpoint,
        {
          plan: planKey,
          billing: billingType,
          currency
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const url = res.data?.checkoutLink;
      if (!url) throw new Error("Checkout URL missing");

      window.location.href = url;
    } catch (err) {
      console.error(err);
      alert(
        err.response?.data?.message ||
        err.message ||
        "Unable to start payment."
      );
    }
  };

  window.addEventListener("startPayment", handler);
  return () => window.removeEventListener("startPayment", handler);
}, []);


  if (loading) {
    return (
      <DashboardLayout>
        <div className="container py-5">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ width: "3rem", height: "3rem", color: BRAND.primary }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3 text-muted">Loading plans...</p>
          </div>
        </div>
      </DashboardLayout>
    )
  }

  return (
    <DashboardLayout>
      {/* Background Gradients */}
      <div
        style={{
          position: "fixed",
          top: "10%",
          left: "5%",
          width: "300px",
          height: "300px",
          background: `linear-gradient(135deg, ${BRAND.primarySoft}, rgba(34,197,94,0.1))`,
          borderRadius: "50%",
          filter: "blur(60px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <div
        style={{
          position: "fixed",
          bottom: "10%",
          right: "5%",
          width: "400px",
          height: "400px",
          background: `linear-gradient(135deg, rgba(245,158,11,0.1), ${BRAND.primarySoft})`,
          borderRadius: "50%",
          filter: "blur(80px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <div className="container py-5" style={{ position: "relative", zIndex: 1 }}>
        {/* Header */}
        <div className="text-center mb-5">
          <div
            className="d-inline-flex align-items-center justify-content-center mb-3"
            style={{
              width: "60px",
              height: "60px",
              background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.success})`,
              borderRadius: "12px",
              boxShadow: `0 4px 12px ${BRAND.primarySoft}`,
            }}
          >
            <i className="bi bi-rocket-takeoff fs-3 text-white"></i>
          </div>
          <h1
            className="fw-bold mb-3"
            style={{
              background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.success})`,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Choose Your Plan
          </h1>
          <p className="text-muted fs-5 mb-0">Select the perfect plan for your needs and start growing today</p>
        </div>

        {/* Billing Toggle */}
        <div className="text-center mb-5">
          <div
            className="d-inline-flex align-items-center bg-white rounded-pill shadow-sm p-1"
            style={{ border: "1px solid #E5E7EB" }}
          >
            <button
              className={`btn btn-sm px-4 py-2 rounded-pill fw-medium ${billingType === "monthly" ? "text-white" : "btn-link text-dark"}`}
              style={
                billingType === "monthly"
                  ? { background: BRAND.primary, boxShadow: `0 2px 8px ${BRAND.primarySoft}`, border: "none" }
                  : { border: "none" }
              }
              onClick={() => setBillingType("monthly")}
            >
              Monthly
            </button>
            <button
              className={`btn btn-sm px-4 py-2 rounded-pill fw-medium ${billingType === "yearly" ? "text-white" : "btn-link text-dark"}`}
              style={
                billingType === "yearly"
                  ? { background: BRAND.primary, boxShadow: `0 2px 8px ${BRAND.primarySoft}`, border: "none" }
                  : { border: "none" }
              }
              onClick={() => setBillingType("yearly")}
            >
              Yearly
              <span className="badge bg-success ms-2" style={{ fontSize: "0.7rem" }}>Save 40%</span>
            </button>
          </div>
        </div>       

        {/* Pricing Cards */}
        <div className="row g-4 mb-5">
          {plans.map((p) => (
            <div key={p.key} className="col-md-6 col-lg-3">
              <PricingCard
                title={p.title}
                price={p.price}
                billingType={billingType}
                features={p.features}
                limit={p.limit}
                monthlyUrls={p.monthlyUrls}
                trafficLimit={p.trafficLimit}
                highlight={["premium", "dedicated"].includes(p.key)}
                isCurrent={p.key === currentUserPlan}
                button={() => openPaymentModal(p.key, billingType)}
              />
            </div>
          ))}
        </div>

        {/* Feature Comparison */}
        <div className="mt-5 pt-5">
          <div className="text-center mb-4">
            <h2
              className="fw-bold mb-3"
              style={{
                background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.success})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Detailed Feature Comparison
            </h2>
            <p className="text-muted">Compare all features across different plans</p>
          </div>
          <PricingComparisonTable plans={plans} billingType={billingType} currentUserPlan={currentUserPlan} />
        </div>

        {/* Highlights / Info */}
        <div className="mt-5 pt-5">
          <div className="row g-4 text-center">
            <div className="col-md-4">
              <div className="p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "50px",
                    height: "50px",
                    background: `linear-gradient(135deg, ${BRAND.primary}, ${BRAND.primaryDark})`,
                    borderRadius: "10px",
                  }}
                >
                  <i className="bi bi-shield-check fs-4 text-white"></i>
                </div>
                <h5 className="fw-bold mb-2">Secure Payments</h5>
                <p className="text-muted mb-0">SSL encrypted and PCI compliant</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "50px",
                    height: "50px",
                    background: `linear-gradient(135deg, ${BRAND.success}, #16A34A)`,
                    borderRadius: "10px",
                  }}
                >
                  <i className="bi bi-arrow-clockwise fs-4 text-white"></i>
                </div>
                <h5 className="fw-bold mb-2">Cancel Anytime</h5>
                <p className="text-muted mb-0">No long-term commitments</p>
              </div>
            </div>
            <div className="col-md-4">
              <div className="p-4">
                <div
                  className="d-inline-flex align-items-center justify-content-center mb-3"
                  style={{
                    width: "50px",
                    height: "50px",
                    background: `linear-gradient(135deg, ${BRAND.warning}, #D97706)`,
                    borderRadius: "10px",
                  }}
                >
                  <i className="bi bi-headset fs-4 text-white"></i>
                </div>
                <h5 className="fw-bold mb-2">24/7 Support</h5>
                <p className="text-muted mb-0">Always here to help you succeed</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
<PaymentMethodModal
  show={showPaymentModal}
  onClose={() => setShowPaymentModal(false)}
  planKey={selectedPlan?.planKey}
  billingType={selectedPlan?.billingType}
  lowestAmount={selectedPlan?.lowestAmount}
/>

      <WhatsAppChat position="right" />            
      <MiniFooter />
    </DashboardLayout>
  )
}
